/**
 * useSupabaseSimulation — Supabase-native simulator hook.
 *
 * Delegates all message writes, flag detection, and eval to agentfactory by
 * passing integration: { type: "simulation" }. agentfactory owns the full
 * pipeline; this hook only ensures the synthetic contact exists and drives
 * agent.execute().
 */

import { useCallback } from "react"
import { supabase } from "@/lib/data"
import { agents } from "@/lib/agents"
import { app } from "@/lib/app"
import { faker } from "@faker-js/faker"
import { ulid } from "ulid"

export interface SimSendResult {
  success: boolean
  contactId: string
  error?: string
  /** Resolves when the async flag + eval pipeline finishes (set by agentfactory). */
  evalPromise?: Promise<void>
}

export interface UseSupabaseSimulationResult {
  sendSimulatedMessage: (opts: {
    contactId: string | null
    teamId: string
    agentId: string
    message: string
    label?: string
    onMessageInserted?: (contactId: string) => void
  }) => Promise<SimSendResult & { newContactId: string }>
  ensureContact: (opts: {
    contactId: string | null
    teamId: string
    label?: string
  }) => Promise<string>
}

export async function ensureSyntheticContact(
  contactId: string | null,
  teamId: string,
  label?: string
): Promise<string> {
  const sex = faker.person.sexType()
  const fallbackAvatar = faker.image.personPortrait({ size: 128, sex })

  if (contactId) {
    const { data } = await supabase
      .from("contacts")
      .select("id, type, name, avatar, data, moderation_data, hitl")
      .eq("id", contactId)
      .single()

    if ((data as any)?.id) {
      const existingContact = data as any
      // Backfill legacy synthetic contacts so simulator state is consistent.
      if (existingContact.type === "synthetic") {
        let parsedData: Record<string, any> = {}
        if (typeof existingContact.data === "string" && existingContact.data.trim()) {
          try {
            parsedData = JSON.parse(existingContact.data)
          } catch {
            parsedData = {}
          }
        }

        let parsedModerationData: Record<string, any> = {}
        if (typeof existingContact.moderation_data === "string" && existingContact.moderation_data.trim()) {
          try {
            parsedModerationData = JSON.parse(existingContact.moderation_data)
          } catch {
            parsedModerationData = {}
          }
        }

        const nextData = {
          ...parsedData,
          simulated: true,
          created_at: parsedData.created_at || new Date().toISOString(),
        }
        const nextModerationData = {
          ...parsedModerationData,
          handled_by: parsedModerationData.handled_by || "bot",
        }

        const needsUpdate =
          !existingContact.name ||
          !existingContact.avatar ||
          existingContact.data == null ||
          existingContact.moderation_data == null ||
          parsedData.simulated !== true ||
          !parsedModerationData.handled_by ||
          typeof existingContact.hitl !== "boolean"

        if (needsUpdate) {
          await supabase
            .from("contacts")
            .update({
              name: existingContact.name || label || "Simulated user",
              avatar: existingContact.avatar || fallbackAvatar,
              data: JSON.stringify(nextData),
              moderation_data: JSON.stringify(nextModerationData),
              hitl: nextModerationData.handled_by === "human",
            })
            .eq("id", existingContact.id)
        }
      }

      return existingContact.id
    }
  }

  const newId = ulid()
  const { error } = await supabase.from("contacts").insert({
    id: newId,
    type: "synthetic",
    team: teamId,
    name: label || faker.person.fullName({ sex }),
    avatar: fallbackAvatar,
    data: JSON.stringify({
      simulated: true,
      created_at: new Date().toISOString(),
    }),
    moderation_data: JSON.stringify({
      handled_by: "bot",
    }),
    hitl: false,
  })
  if (error) throw new Error(`Failed to create synthetic contact: ${error.message}`)
  return newId
}

export function useSupabaseSimulation(): UseSupabaseSimulationResult {
  const ensureContact = useCallback(async ({
    contactId,
    teamId,
    label,
  }: {
    contactId: string | null
    teamId: string
    label?: string
  }) => ensureSyntheticContact(contactId, teamId, label), [])

  const sendSimulatedMessage = useCallback(async ({
    contactId,
    teamId,
    agentId,
    message,
    label,
    onMessageInserted,
  }: {
    contactId: string | null
    teamId: string
    agentId: string
    message: string
    label?: string
    onMessageInserted?: (contactId: string) => void
  }): Promise<SimSendResult & { newContactId: string }> => {
    let resolvedContactId = contactId || ""

    try {
      // ── 1. Ensure contact ──────────────────────────────────────────────
      resolvedContactId = await ensureSyntheticContact(contactId, teamId, label)

      // ── 2. Notify caller so UI can update contactId before agent runs ──
      onMessageInserted?.(resolvedContactId)

      // ── 3. Load agent + API keys ───────────────────────────────────────
      const apiKeys = await app.fetchAPIkeys(teamId)
      const agent = await agents.loadAgent(agentId as any, teamId)
      if (!agent) throw new Error("Agent not found")

      // ── 4. Execute — agentfactory owns all writes (user msg, agent msg,
      //    flags, evals) when integration.type === "simulation" ──────────
      const p: AgentParameters = {
        debug: true,
        uid: resolvedContactId,
        team: teamId,
        input: { message },
        apiKeys,
        integration: { type: "app", contact: resolvedContactId },
      }

      await agent.execute(p)
      if (p.error) throw new Error(p.error)

      return { success: true, contactId: resolvedContactId, newContactId: resolvedContactId, evalPromise: p.evalPromise }
    } catch (err) {
      return {
        success: false,
        contactId: resolvedContactId,
        newContactId: resolvedContactId,
        error: err instanceof Error ? err.message : "Unknown error",
      }
    }
  }, [])

  return { sendSimulatedMessage, ensureContact }
}

